/**
 * Webpack Error Detection Utilities
 * 
 * Centralized logic for detecting webpack-related errors that occur during
 * deployments when users have stale chunks cached in their browsers.
 */

export interface ErrorLike {
  message?: string
  stack?: string
  name?: string
}

export function isErrorLike(error: unknown): error is ErrorLike {
  return error !== null && typeof error === 'object'
}

/**
 * Checks if an error is related to webpack module loading failures.
 * This typically happens when users have cached webpack chunks that are
 * no longer available after a deployment.
 */
export function isWebpackError(error: unknown): boolean {
  if (!isErrorLike(error)) return false
  
  const message = error.message || ""
  const stack = error.stack || ""
  const name = error.name || ""
  
  return (
    // Chunk load failures from dynamic imports (e.g. lucide-react icons)
    name === "ChunkLoadError" ||
    message.includes("Loading chunk") ||
    message.includes("Failed to fetch dynamically imported module") ||

    // Classic webpack call errors
    message.includes("Cannot read properties of undefined (reading 'call')") ||
    message.includes("Cannot read property 'call' of undefined") ||

    // Module parsing failures
    message.includes("Module parse failed") ||
    
    // Webpack-specific stack trace patterns
    stack.includes("webpackJsonp") ||
    stack.includes("__webpack_require__") ||
    stack.includes("webpack_require") ||
    
    // TypeError with call-related issues in webpack context
    (name === "TypeError" && (
      message.includes("Cannot read property") || 
      message.includes("Cannot read properties")
    ) && stack.includes("call")) ||
    
    // Module parse failed with webpack context
    (message.includes("Module parse failed") && message.includes("webpack"))
  )
}

/**
 * Checks if an error is network-related and should be ignored
 * for webpack error detection purposes.
 */
export function isNetworkRelatedError(error: unknown): boolean {
  if (!isErrorLike(error)) return false
  
  const message = error.message || ""
  return (
    (message.includes("Failed to fetch") && !message.includes("dynamically imported module")) ||
    message.includes("NetworkError") ||
    message.includes("ERR_NETWORK") ||
    message.includes("ERR_INTERNET_DISCONNECTED")
  )
}

/**
 * Type guard to check if an error is webpack-related and not network-related.
 * This is the main function components should use for webpack error detection.
 */
export function isHandleableWebpackError(error: unknown): boolean {
  return !isNetworkRelatedError(error) && isWebpackError(error)
}

/**
 * Gets the error type classification for analytics purposes.
 */
export function getErrorTypeForAnalytics(error: unknown, context: string): string {
  if (isWebpackError(error)) {
    return `webpack_module_loading_${context}`
  }
  return `${context}_error`
}